import { apiFetch } from "../../lib/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ── Types mirroring the Rust ArchMap model ────────────────────

interface ModuleNode {
  name: string;
  path: string;
  kind: "Binary" | "Library" | "Component" | "Config" | "Test" | "Style";
  lines: number;
  dependencies: string[];
}

interface ArchMapData {
  nodes: ModuleNode[];
  edges: [string, string][];
  generated_at: string;
}

// ── Constants ─────────────────────────────────────────────────

const KIND_COLORS: Record<ModuleNode["kind"], string> = {
  Binary: "#fbbf24",
  Library: "#4f8fff",
  Component: "#bd93f9",
  Config: "#5eead4",
  Test: "#f472b6",
  Style: "#8be9fd",
};

const KIND_LABELS: Record<ModuleNode["kind"], string> = {
  Binary: "bin",
  Library: "lib",
  Component: "component",
  Config: "config",
  Test: "test",
  Style: "style",
};

// ── Component ─────────────────────────────────────────────────

export function ArchMapWorkbench() {
  const [archmap, setArchmap] = useState<ArchMapData | null>(null);
  const [svgMarkup, setSvgMarkup] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ModuleNode | null>(null);
  const [filterKind, setFilterKind] = useState<ModuleNode["kind"] | "all">("all");
  const svgContainerRef = useRef<HTMLDivElement>(null);

  const fetchArchmap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/archmap");
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();
      setArchmap(data.archmap ?? data);
      setSvgMarkup(data.svg ?? "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch architecture map");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchmap();
  }, [fetchArchmap]);

  // Click handler: detect clicks on SVG nodes (g[data-path])
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container || !archmap) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      const group = target.closest("g[data-path]");
      if (group) {
        const path = group.getAttribute("data-path");
        const node = archmap.nodes.find((n) => n.path === path);
        if (node) setSelected(node);
      }
    };

    container.addEventListener("click", handleClick);
    return () => container.removeEventListener("click", handleClick);
  }, [archmap]);

  // Computed stats
  const stats = useMemo(() => {
    if (!archmap) return { modules: 0, lines: 0, edges: 0, kinds: {} as Record<string, number> };
    const kinds: Record<string, number> = {};
    let totalLines = 0;
    for (const node of archmap.nodes) {
      totalLines += node.lines;
      kinds[node.kind] = (kinds[node.kind] ?? 0) + 1;
    }
    return {
      modules: archmap.nodes.length,
      lines: totalLines,
      edges: archmap.edges.length,
      kinds,
    };
  }, [archmap]);

  // Dependents of the selected node
  const dependents = useMemo(() => {
    if (!selected || !archmap) return [];
    return archmap.nodes.filter((n) => n.dependencies.some((d) => {
      const selName = selected.path.split("/").pop()?.replace(/\.[^.]+$/, "") ?? "";
      return d === selName || d === selected.name;
    }));
  }, [selected, archmap]);

  // Filtered nodes for the sidebar list
  const filteredNodes = useMemo(() => {
    if (!archmap) return [];
    if (filterKind === "all") return archmap.nodes;
    return archmap.nodes.filter((n) => n.kind === filterKind);
  }, [archmap, filterKind]);

  return (
    <main className="ui-shell" style={shellStyle}>
      <section style={headerStyle}>
        <div>
          <span style={kickerStyle}>Architecture</span>
          <h1 style={titleStyle}>Living Architecture Map</h1>
          <p style={subtitleStyle}>
            Auto-generated dependency graph of this project's modules.
          </p>
        </div>
        <div style={headerActionsStyle}>
          <button
            type="button"
            style={buttonStyle}
            onClick={fetchArchmap}
            disabled={loading}
          >
            {loading ? "Scanning..." : "Regenerate"}
          </button>
        </div>
      </section>

      {error && (
        <div style={errorStyle}>
          {error}
        </div>
      )}

      {/* Stats bar */}
      <section style={statsBarStyle}>
        <div style={statStyle}>
          <strong style={{ color: "#4f8fff" }}>{stats.modules}</strong>
          <span>modules</span>
        </div>
        <div style={statStyle}>
          <strong style={{ color: "#bd93f9" }}>{stats.lines.toLocaleString()}</strong>
          <span>total lines</span>
        </div>
        <div style={statStyle}>
          <strong style={{ color: "#fbbf24" }}>{stats.edges}</strong>
          <span>dependencies</span>
        </div>
        {Object.entries(stats.kinds).map(([kind, count]) => (
          <div key={kind} style={statStyle}>
            <strong style={{ color: KIND_COLORS[kind as ModuleNode["kind"]] ?? "#888" }}>
              {count}
            </strong>
            <span>{KIND_LABELS[kind as ModuleNode["kind"]] ?? kind}</span>
          </div>
        ))}
      </section>

      {/* Main body: sidebar + SVG + inspector */}
      <section style={bodyStyle}>
        {/* Sidebar: module list */}
        <aside style={sidebarStyle}>
          <div style={filterBarStyle}>
            <button
              type="button"
              style={filterKind === "all" ? filterBtnActiveStyle : filterBtnStyle}
              onClick={() => setFilterKind("all")}
            >
              all
            </button>
            {(Object.keys(KIND_LABELS) as ModuleNode["kind"][]).map((k) => (
              <button
                key={k}
                type="button"
                style={filterKind === k ? filterBtnActiveStyle : filterBtnStyle}
                onClick={() => setFilterKind(k)}
              >
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
          <div style={listStyle}>
            {filteredNodes.map((node) => (
              <button
                key={node.path}
                type="button"
                style={{
                  ...listItemStyle,
                  borderLeftColor: KIND_COLORS[node.kind],
                  background: selected?.path === node.path ? "#282a36" : "transparent",
                }}
                onClick={() => setSelected(node)}
              >
                <strong style={{ color: KIND_COLORS[node.kind] }}>{node.name}</strong>
                <span style={listMetaStyle}>
                  {node.lines}L · {KIND_LABELS[node.kind]}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* SVG viewport */}
        <div style={svgViewportStyle} ref={svgContainerRef}>
          {svgMarkup ? (
            <div
              dangerouslySetInnerHTML={{ __html: svgMarkup }}
              style={{ width: "100%", height: "100%", overflow: "auto" }}
            />
          ) : loading ? (
            <div style={placeholderStyle}>Scanning project...</div>
          ) : (
            <div style={placeholderStyle}>No architecture data yet.</div>
          )}
        </div>

        {/* Inspector panel */}
        {selected && (
          <article style={inspectorStyle}>
            <div style={inspectorHeaderStyle}>
              <span
                style={{
                  ...inspectorSwatchStyle,
                  background: KIND_COLORS[selected.kind],
                }}
              />
              <div>
                <span style={inspectorKindStyle}>{KIND_LABELS[selected.kind]}</span>
                <h2 style={inspectorNameStyle}>{selected.name}</h2>
              </div>
            </div>

            <div style={inspectorBlockStyle}>
              <h3 style={inspectorH3Style}>Path</h3>
              <code style={inspectorCodeStyle}>{selected.path}</code>
            </div>

            <div style={inspectorBlockStyle}>
              <h3 style={inspectorH3Style}>Size</h3>
              <p style={inspectorValueStyle}>{selected.lines.toLocaleString()} lines</p>
            </div>

            <div style={inspectorBlockStyle}>
              <h3 style={inspectorH3Style}>Dependencies ({selected.dependencies.length})</h3>
              {selected.dependencies.length > 0 ? (
                <div style={tagListStyle}>
                  {selected.dependencies.map((dep) => (
                    <em key={dep} style={tagStyle}>{dep}</em>
                  ))}
                </div>
              ) : (
                <p style={inspectorMutedStyle}>No dependencies.</p>
              )}
            </div>

            <div style={inspectorBlockStyle}>
              <h3 style={inspectorH3Style}>Dependents ({dependents.length})</h3>
              {dependents.length > 0 ? (
                <div style={tagListStyle}>
                  {dependents.map((dep) => (
                    <em
                      key={dep.path}
                      style={tagStyle}
                      onClick={() => setSelected(dep)}
                    >
                      {dep.name}
                    </em>
                  ))}
                </div>
              ) : (
                <p style={inspectorMutedStyle}>No dependents found.</p>
              )}
            </div>

            <button
              type="button"
              style={closeButtonStyle}
              onClick={() => setSelected(null)}
            >
              Close
            </button>
          </article>
        )}
      </section>
    </main>
  );
}

// ── Inline styles (terminal-neon dark theme) ──────────────────

const shellStyle: React.CSSProperties = {
  background: "#0d0d0d",
  color: "#f8f8f2",
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "24px 32px 16px",
  borderBottom: "1px solid #282a36",
};

const kickerStyle: React.CSSProperties = {
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#4f8fff",
  fontWeight: 700,
};

const titleStyle: React.CSSProperties = {
  fontSize: "22px",
  fontWeight: 700,
  color: "#f8f8f2",
  margin: "4px 0 2px",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "#6272a4",
  margin: 0,
};

const headerActionsStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  alignItems: "center",
};

const buttonStyle: React.CSSProperties = {
  background: "#4f8fff",
  color: "#0d0d0d",
  border: "none",
  borderRadius: "6px",
  padding: "8px 20px",
  fontSize: "12px",
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  margin: "0 32px",
  padding: "10px 16px",
  background: "#2d1117",
  border: "1px solid #ff5555",
  borderRadius: "6px",
  color: "#ff5555",
  fontSize: "12px",
};

const statsBarStyle: React.CSSProperties = {
  display: "flex",
  gap: "24px",
  padding: "12px 32px",
  borderBottom: "1px solid #282a36",
  flexWrap: "wrap",
};

const statStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  fontSize: "12px",
};

const bodyStyle: React.CSSProperties = {
  display: "flex",
  flex: 1,
  overflow: "hidden",
};

const sidebarStyle: React.CSSProperties = {
  width: "220px",
  minWidth: "220px",
  borderRight: "1px solid #282a36",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const filterBarStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "4px",
  padding: "8px",
  borderBottom: "1px solid #282a36",
};

const filterBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid #282a36",
  borderRadius: "4px",
  padding: "3px 8px",
  fontSize: "10px",
  color: "#6272a4",
  cursor: "pointer",
  fontFamily: "inherit",
};

const filterBtnActiveStyle: React.CSSProperties = {
  ...filterBtnStyle,
  background: "#282a36",
  color: "#4f8fff",
  borderColor: "#4f8fff",
};

const listStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "4px 0",
};

const listItemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  width: "100%",
  padding: "6px 12px",
  border: "none",
  borderLeft: "3px solid transparent",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "inherit",
  fontSize: "11px",
};

const listMetaStyle: React.CSSProperties = {
  fontSize: "10px",
  color: "#6272a4",
};

const svgViewportStyle: React.CSSProperties = {
  flex: 1,
  overflow: "auto",
  background: "#0d0d0d",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "16px",
};

const placeholderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#6272a4",
  fontSize: "14px",
  height: "100%",
  width: "100%",
};

const inspectorStyle: React.CSSProperties = {
  width: "280px",
  minWidth: "280px",
  borderLeft: "1px solid #282a36",
  padding: "16px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const inspectorHeaderStyle: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
};

const inspectorSwatchStyle: React.CSSProperties = {
  width: "10px",
  height: "10px",
  borderRadius: "3px",
  marginTop: "6px",
  flexShrink: 0,
};

const inspectorKindStyle: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#6272a4",
};

const inspectorNameStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#f8f8f2",
  margin: "2px 0 0",
};

const inspectorBlockStyle: React.CSSProperties = {
  borderTop: "1px solid #282a36",
  paddingTop: "10px",
};

const inspectorH3Style: React.CSSProperties = {
  fontSize: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#6272a4",
  margin: "0 0 6px",
};

const inspectorCodeStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "#4f8fff",
  wordBreak: "break-all",
};

const inspectorValueStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "#f8f8f2",
  margin: 0,
};

const inspectorMutedStyle: React.CSSProperties = {
  fontSize: "11px",
  color: "#6272a4",
  margin: 0,
};

const tagListStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "4px",
};

const tagStyle: React.CSSProperties = {
  background: "#282a36",
  padding: "2px 8px",
  borderRadius: "4px",
  fontSize: "10px",
  color: "#bd93f9",
  fontStyle: "normal",
  cursor: "pointer",
};

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid #282a36",
  borderRadius: "6px",
  padding: "6px 16px",
  fontSize: "11px",
  color: "#6272a4",
  cursor: "pointer",
  fontFamily: "inherit",
  marginTop: "auto",
};
